import { Button as BaseButton } from '@base-ui/react/button'
import { Toggle as BaseToggle } from '@base-ui/react/toggle'
import type { ToggleProps as BaseToggleProps } from '@base-ui/react/toggle'
import { forwardRef } from 'react'
import type { ReactElement, ReactNode } from 'react'
import styles from './styles/button.module.css'
import { SIZE_CLASS, VARIANT_CLASS } from './styles/button-variant'
import type { ButtonSize } from './styles/button-variant'
import type { ChipVariant } from './styles/chip-badge'
import chipStyles from './styles/chip-badge.module.css'
import { chipControlClasses } from './styles/chip-control'
import { TONE_CLASS } from './styles/tones'
import type { ActionTone } from './styles/tones'

/**
 * El peso de un toggle con `look="button"`: **tres, no los cuatro del `Button`.**
 *
 * `solid` no está, y la razón es el estado y no el gusto. El encendido de este control se dibuja
 * tomando el relleno sólido, que es el único peldaño del eje que un hover no puede imitar —el hover
 * de las variantes sin relleno marca la línea y deja el fondo quieto—. Un `solid` ya *es* ese
 * peldaño en reposo, así que no le queda nada para el encendido: las salidas habrían sido bajar el
 * apagado —y entonces el peso lo elegiría el estado y no quien escribe el call site— o dejar un
 * encendido indistinguible de un hover. La cardinalidad de un eje la fija el componente (ADR-0015).
 *
 * El default es `outline`, no `solid`: un toggle es un control de barra o de encabezado, y arranca
 * discreto para que lo que se note sea el cambio de estado.
 */
export type ToggleButtonVariant = 'soft' | 'outline' | 'ghost'

/**
 * Lo que el toggle toma de Base UI, menos tres props que la capa no reexpone:
 *
 * - **`render`** lo usa este componente internamente y no se puede pisar. Es lo que recupera
 *   `focusableWhenDisabled`, que el `Toggle` de Base UI no tiene (abajo).
 * - **`value`** solo sirve dentro de un `ToggleGroup`, y la capa no tiene uno: un grupo de controles
 *   que compiten entre sí es una pregunta con respuestas, y para eso están `ChoiceGroup` y
 *   `CheckboxGroup`. Ofrecer la prop sería prometer una composición que no existe.
 * - **`nativeButton`** es del mecanismo de `render`, que acá está resuelto.
 */
type ToggleShared = Omit<
  BaseToggleProps<string>,
  'children' | 'className' | 'render' | 'value' | 'nativeButton' | 'aria-label' | 'aria-labelledby'
> & {
  /** De qué habla el estado encendido. Default `primary`. */
  tone?: ActionTone
  className?: string
  /**
   * Default `true`, como en `Button` y `ChipButton`. **Acá no es solo coherencia: es lo que el
   * `Toggle` de Base UI no da.** Ese componente deshabilita con el atributo nativo —su propio
   * código lo dice, «a disabled toggle is natively disabled and cannot hold roving focus»—, y un
   * botón con `disabled` nativo sale del orden de tabulación y no recibe eventos de puntero, así
   * que un usuario de teclado nunca descubre que el control existe y un `Tooltip` encima no se abre
   * nunca. La capa ya había decidido no pagar ese precio en el `Button`.
   *
   * Se recupera renderizando el `Toggle` sobre el `Button` de Base UI, que sí tiene la prop:
   * el resultado lleva `aria-disabled` en vez del atributo nativo, y conserva `data-pressed` y
   * `data-disabled`. Se apaga por instancia con `focusableWhenDisabled={false}`.
   */
  focusableWhenDisabled?: boolean
}

/**
 * Mismo contrato que el `icon` del `Button`, y por el mismo motivo: `icon: true` exige un
 * `children` que sea `ReactElement` —rechaza un glifo Unicode suelto, que es `string`— y uno de
 * `aria-label`/`aria-labelledby`. En un toggle el caso pesa más que en un botón, porque un control
 * de solo icono es la forma habitual de un «fijar» o un «mostrar».
 */
type ToggleIconContract =
  | ({ icon: true; children: ReactElement } & (
      | { 'aria-label': string; 'aria-labelledby'?: undefined }
      | { 'aria-label'?: undefined; 'aria-labelledby': string }
    ))
  | { icon?: false; children?: ReactNode; 'aria-label'?: string; 'aria-labelledby'?: string }

/**
 * `look` elige la caja, y con ella la cardinalidad del peso: la del `Button` tiene tres valores, la
 * del `Chip` tiene dos. Por eso es un union discriminado y no dos props sueltas — así
 * `look="chip" variant="ghost"` no compila, que es correcto: un chip sin fondo ni borde es texto
 * suelto y esa ausencia vive en el tipo (`ChipVariant`), no en una guía de uso.
 *
 * El `look="chip"` tampoco lleva `size` ni `icon`, igual que `ChipButton`: la caja del chip no tiene
 * eje de tamaño en la capa, y un chip de solo icono no se distingue de un punto.
 */
export type ToggleProps =
  | (ToggleShared & { look?: 'button'; variant?: ToggleButtonVariant; size?: ButtonSize } & ToggleIconContract)
  | (ToggleShared & {
      look: 'chip'
      variant?: ChipVariant
      /**
       * `never` y no ausentes: los dos ejes que la piel de chip no tiene se declaran para que
       * **prohibirlos siga siendo lo mismo que no tenerlos** —`look="chip" size="lg"` no compila— y
       * al mismo tiempo el componente pueda desarmar sus props de una sola vez, sin dejar variables
       * sin usar. En `src/au/` una variable muerta es un error de lint, con razón: viaja a todos los
       * consumidores.
       */
      size?: never
      icon?: never
      children?: ReactNode
      'aria-label'?: string
      'aria-labelledby'?: string
    })

/**
 * Un control que **se queda prendido**: un favorito, una serie visible, un widget fijado.
 *
 * Emite un `<button>` con `aria-pressed`, que es la semántica de un toggle que **no compite con
 * vecinos**. Ahí está la frontera con el resto de la capa, y conviene tenerla escrita porque los
 * cuatro se parecen al mirarlos:
 *
 * - **`Button`** dispara y no recuerda. Si al soltarlo la vista no queda distinta *en el control*,
 *   es un botón.
 * - **`Switch`** es una preferencia que se guarda, con `role="switch"` y la forma de un
 *   interruptor. Un toggle es un botón que quedó apretado; el switch es una llave. Cuando el
 *   estado es un ajuste de configuración con etiqueta al lado, es `Switch`.
 * - **`ChoiceGroup`** y **`CheckboxGroup`** son la pregunta con respuestas. Una tira de toggles
 *   donde prender uno apaga a otro es un `radiogroup`, y una donde se prenden varios es un grupo de
 *   casillas: los dos existen, con su navegación por teclado y su valor. Un toggle en tira es una
 *   regresión de accesibilidad, no un atajo.
 *
 * El estado puede ser controlado (`pressed` + `onPressedChange`) o no (`defaultPressed`).
 */
// `forwardRef` es un shim de React 18: la capa corre React 19, donde la `ref` ya viaja dentro de
// `props`. Se retira con el codemod `react/19/remove-forward-ref` cuando el último consumidor
// migre (ADR-0028). `HTMLButtonElement`, no `HTMLElement` como en `Button`/`ChipButton`: a
// diferencia del `Button` de Base UI, que es polimórfico vía `render`, `BaseToggle` siempre
// renderiza un `<button>` y tipa su propia ref como `HTMLButtonElement`.
export const Toggle = forwardRef<HTMLButtonElement, ToggleProps>(function Toggle({
  look = 'button',
  tone = 'primary',
  variant,
  size = 'md',
  icon = false,
  focusableWhenDisabled = true,
  className,
  ...rest
}, ref) {
  // El `render` es lo que hace el trabajo de accesibilidad de este componente: el `Toggle` de Base
  // UI aporta el estado —`aria-pressed` y el `data-pressed` que dibuja el CSS— y el `Button` de
  // Base UI, el manejo de deshabilitado que el `Toggle` no tiene. Verificado por SSR: el resultado
  // conserva `data-pressed` y cambia el `disabled` nativo por `aria-disabled`.
  //
  // **Componer dos componentes de Base UI sobre un nodo es un patrón de la librería**, no un
  // truco: su handbook de composición dice que los `render` se pueden anidar tanto como haga falta,
  // y `useButton` trae el caso escrito —«the `disabled` prop needs to pass through 2 `useButton`s
  // then finally delete the `disabled` attribute from DOM»— para la forma exacta que se usa acá.
  // De ahí sale lo que importa y que no se ve desde afuera: el guard de deshabilitado corre
  // **antes** de llamar al handler que recibió, así que el `Button` corta el click antes de que el
  // `onClick` del `Toggle` lo vea, y con un botón nativo ninguna de las dos capas sintetiza el
  // click desde el teclado. Ni doble disparo, ni un toggle apagado que igual cambia de estado.
  const render = <BaseButton focusableWhenDisabled={focusableWhenDisabled} />

  // La correlación entre `look` y el tipo de `variant` la garantiza el union discriminado en el
  // call site; desarmar las props en un solo objeto la pierde acá adentro, y el `as` es la
  // contracara de haberlas desarmado una sola vez. Los defaults de la variante son distintos por
  // piel —`outline` y `soft`— y por eso no pueden ir arriba.
  if (look === 'chip') {
    const chipVariant = (variant as ChipVariant | undefined) ?? 'soft'
    return (
      <BaseToggle
        ref={ref}
        className={chipControlClasses(tone, chipVariant, className, chipStyles['au-chip-control--toggle'])}
        render={render}
        {...rest}
      />
    )
  }

  // Las tres clases del botón salen de las mismas tablas que el `Button`: la de tono solo reasigna
  // las custom properties `--au-tint-*` y la de variante solo las lee. Lo que agrega el toggle es
  // la clase marcadora, que es a la que engancha el dibujo del encendido — ver el comentario de
  // `au-btn--toggle` en `styles/button.module.css`.
  const classes = [
    styles['au-btn'],
    styles['au-btn--toggle'],
    'au-focusable',
    TONE_CLASS[tone],
    VARIANT_CLASS[(variant as ToggleButtonVariant | undefined) ?? 'outline'],
    SIZE_CLASS[size],
    icon && styles['au-btn--icon'],
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return <BaseToggle ref={ref} className={classes} render={render} {...rest} />
})

Toggle.displayName = 'Toggle'
